<template>
    <div>
        <form-modal
            ref="formModal"
            :title="modalTitle"
            size="lg"
            :in-editing="isEdit"
            :handle-close="handleClose"
        >
            <template v-slot:form>
                <form @submit.prevent="handleSubmit">
                    <div
                        v-if="questionHasId"
                        class="loja-perfeita-modal-toolbar d-flex justify-content-end align-items-center flex-nowrap w-100 mb-2"
                    >
                        <edit-label-button
                            v-if="!isEdit"
                            :title="getI18n(i18nKey, 'TITLES.questao')"
                            :disabled="submitLoading"
                            @onClick="enterEditMode"
                        />
                        <delete-label-button
                            :title="getI18n(i18nKey, 'TITLES.questao')"
                            :disabled="submitLoading"
                            @onClick="confirmDeleteQuestion"
                        />
                    </div>
                    <b-row>
                        <b-col md="12">
                            <b-form-group
                                :label="getI18n(i18nKey, 'TITLES.tarefa_do_roteiro')"
                                label-for="questionnaire-select"
                                class="required label-pdv"
                            >
                            <loja-perfeita-form-control-loading-wrap :busy="questionnairesLoading">
                                <b-form-select
                                    id="questionnaire-select"
                                    v-model="formData.questionnaire_id"
                                    class="input-pdv-blue"
                                    :options="questionnaireOptions"
                                    :disabled="submitLoading || questionnairesLoading || !isFormEditable"
                                    @change="onQuestionnaireChange"
                                >
                                    <template v-slot:first>
                                        <b-form-select-option :value="null" disabled>
                                            {{ getI18n(i18nKey, 'PLACEHOLDERS.selecionar_tarefa') }}
                                        </b-form-select-option>
                                    </template>
                                </b-form-select>
                            </loja-perfeita-form-control-loading-wrap>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="12">
                        <b-form-group
                            :label="getI18n(i18nKey, 'TITLES.questao_do_roteiro')"
                            label-for="task-roster"
                            class="required label-pdv"
                        >
                            <loja-perfeita-form-control-loading-wrap :busy="rosterTasksLoading">
                                <b-form-select
                                    id="task-roster"
                                    v-model="formData.questionnaire_item_id"
                                    class="input-pdv-blue"
                                    :options="rosterTaskOptions"
                                    :disabled="submitLoading || rosterTasksLoading || !formData.questionnaire_id || !isFormEditable"
                                    :state="rosterTaskSelectState"
                                    @change="onRosterTaskChange"
                                >
                                    <template v-slot:first>
                                        <b-form-select-option :value="null" disabled>
                                            {{ getI18n(i18nKey, 'PLACEHOLDERS.selecionar_questao') }}
                                        </b-form-select-option>
                                    </template>
                                </b-form-select>
                            </loja-perfeita-form-control-loading-wrap>
                            <b-form-invalid-feedback v-if="rosterTasksLoaded && rosterTasks.length === 0">
                                {{ getI18n(i18nKey, 'MESSAGES.nenhuma_questao_na_tarefa') }}
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row v-if="selectedTaskType && scopeSelectVisible">
                    <b-col md="12">
                        <b-form-group
                            :label="scopeLabel"
                            label-for="scope-entity"
                            class="label-pdv"
                        >
                            <loja-perfeita-form-control-loading-wrap :busy="scopeOptionsLoading">
                                <b-form-select
                                    id="scope-entity"
                                    v-model="formData[scopeFieldName]"
                                    class="input-pdv-blue"
                                    :options="scopeEntityOptions"
                                    :disabled="submitLoading || scopeOptionsLoading || !isFormEditable"
                                >
                                    <template v-slot:first>
                                        <b-form-select-option :value="null">
                                            {{ getI18n(i18nKey, 'SCOPE.generalized') }}
                                        </b-form-select-option>
                                    </template>
                                </b-form-select>
                            </loja-perfeita-form-control-loading-wrap>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="12">
                        <h6 class="text-secondary mb-2 text-uppercase small font-weight-bold">
                            {{ getI18n(i18nKey, 'TITLES.question_dependency_block') }}
                        </h6>
                        <b-alert show variant="light" class="border small mb-3">
                            {{ getI18n(i18nKey, 'INTROS.question_dependency_same_criterion') }}
                        </b-alert>
                        <div class="d-flex align-items-center mb-2">
                            <b-form-radio-group
                                v-model="dependencyMode"
                                :options="dependencyModeOptions"
                                :disabled="submitLoading || !isFormEditable"
                                buttons
                                button-variant="outline-primary"
                                size="sm"
                            />
                        </div>

                        <template v-if="dependencyMode === 'simple'">
                            <b-form-group
                                :label="getI18n(i18nKey, 'FIELDS.depends_on_question_hint')"
                                label-for="question-depends-on-sibling"
                                class="label-pdv"
                            >
                                <b-form-select
                                    id="question-depends-on-sibling"
                                    v-model="formData.depends_on_criterion_task_id"
                                    class="input-pdv-blue"
                                    :options="dependsOnSiblingOptions"
                                    :disabled="submitLoading || !isFormEditable"
                                    @change="onDependsOnQuestionChange"
                                >
                                    <template v-slot:first>
                                        <b-form-select-option :value="null">
                                            {{ getI18n(i18nKey, 'PLACEHOLDERS.nenhuma_dependencia_questao') }}
                                        </b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-form-group>
                            <b-alert
                                v-if="formData.depends_on_criterion_task_id && dependencyKind === 'file'"
                                show
                                variant="warning"
                                class="small py-2"
                            >
                                {{ getI18n(i18nKey, 'MESSAGES.question_rule_file_hint') }}
                            </b-alert>
                            <b-row v-if="formData.depends_on_criterion_task_id">
                                <template v-if="dependsOnMetaLoading">
                                    <b-col md="6">
                                        <b-form-group
                                            :label="getI18n(i18nKey, 'FIELDS.depends_on_question_operator')"
                                            class="label-pdv"
                                        >
                                            <loja-perfeita-form-control-loading-wrap :busy="true">
                                                <b-form-select
                                                    disabled
                                                    class="input-pdv-blue"
                                                    :value="dependencyMetaLoadingSelectValue"
                                                >
                                                    <b-form-select-option
                                                        :value="dependencyMetaLoadingSelectValue"
                                                        disabled
                                                    >
                                                        {{ getI18n(i18nKey, 'MESSAGES.loading_options') }}
                                                    </b-form-select-option>
                                                </b-form-select>
                                            </loja-perfeita-form-control-loading-wrap>
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="6">
                                        <b-form-group
                                            :label="dependencyValueLabelDep"
                                            class="label-pdv"
                                        >
                                            <loja-perfeita-form-control-loading-wrap :busy="true">
                                                <b-form-select
                                                    disabled
                                                    class="input-pdv-blue"
                                                    :value="dependencyMetaLoadingSelectValue"
                                                >
                                                    <b-form-select-option
                                                        :value="dependencyMetaLoadingSelectValue"
                                                        disabled
                                                    >
                                                        {{ getI18n(i18nKey, 'MESSAGES.loading_options') }}
                                                    </b-form-select-option>
                                                </b-form-select>
                                            </loja-perfeita-form-control-loading-wrap>
                                        </b-form-group>
                                    </b-col>
                                </template>
                                <template v-else>
                                    <b-col md="6">
                                        <b-form-group
                                            :label="getI18n(i18nKey, 'FIELDS.depends_on_question_operator')"
                                            label-for="question-dep-operator"
                                            class="label-pdv"
                                        >
                                            <b-form-select
                                                id="question-dep-operator"
                                                v-model="formData.depends_on_condition_operator"
                                                class="input-pdv-blue"
                                                :options="dependencyOperatorOptions"
                                                :disabled="submitLoading || !isFormEditable"
                                                @change="onDependencyOperatorChange"
                                            />
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="6">
                                        <b-form-group
                                            :label="dependencyValueLabelDep"
                                            label-for="question-dep-value"
                                            class="label-pdv"
                                        >
                                            <b-form-select
                                                v-if="dependencyValueUsesBooleanSelect"
                                                id="question-dep-value"
                                                v-model="formData.depends_on_condition_value"
                                                class="input-pdv-blue"
                                                :options="booleanValueOptions"
                                                :disabled="submitLoading || !isFormEditable"
                                            />
                                            <b-form-input
                                                v-else-if="dependencyValueUsesNumberInput"
                                                id="question-dep-value"
                                                v-model="formData.depends_on_condition_value"
                                                class="input-pdv-blue"
                                                type="number"
                                                :min="dependencyKind === 'file' ? 0 : undefined"
                                                :step="dependencyKind === 'file' ? 1 : 'any'"
                                                :disabled="submitLoading || !isFormEditable"
                                            />
                                            <b-form-select
                                                v-else-if="dependencyValueUsesChoiceSelect"
                                                id="question-dep-value"
                                                v-model="formData.depends_on_condition_value"
                                                class="input-pdv-blue"
                                                :options="dependencyChoiceSelectOptions"
                                                :disabled="submitLoading || !isFormEditable"
                                            >
                                                <template v-slot:first>
                                                    <b-form-select-option :value="null" disabled>
                                                        {{ getI18n(i18nKey, 'PLACEHOLDERS.selecionar_questao') }}
                                                    </b-form-select-option>
                                                </template>
                                            </b-form-select>
                                            <b-form-input
                                                v-else-if="dependencyKind === 'date'"
                                                id="question-dep-value"
                                                v-model="formData.depends_on_condition_value"
                                                class="input-pdv-blue"
                                                type="date"
                                                :disabled="submitLoading || !isFormEditable"
                                            />
                                            <b-form-input
                                                v-else
                                                id="question-dep-value"
                                                v-model="formData.depends_on_condition_value"
                                                class="input-pdv-blue"
                                                type="text"
                                                :disabled="submitLoading || !isFormEditable"
                                            />
                                        </b-form-group>
                                    </b-col>
                                </template>
                            </b-row>
                            <small
                                v-if="formData.depends_on_criterion_task_id && dependencyKind === 'unknown'"
                                class="text-muted d-block mb-2"
                            >
                                {{ getI18n(i18nKey, 'MESSAGES.question_rule_meta_unknown') }}
                            </small>
                            <p v-if="questionDependencySummaryLine" class="small text-muted mb-3">
                                {{ questionDependencySummaryLine }}
                            </p>
                        </template>

                        <template v-if="dependencyMode === 'compound'">
                            <b-form-group
                                :label="getI18n(i18nKey, 'FIELDS.dependency_logic')"
                                label-for="dep-rule-logic"
                                class="label-pdv"
                            >
                                <b-form-select
                                    id="dep-rule-logic"
                                    v-model="compoundRule.logic"
                                    class="input-pdv-blue"
                                    :options="compoundLogicOptions"
                                    :disabled="submitLoading || !isFormEditable"
                                />
                            </b-form-group>

                            <div
                                v-for="(item, idx) in compoundRule.items"
                                :key="idx"
                                class="border rounded p-2 mb-2 bg-light"
                            >
                                <b-row>
                                    <b-col md="4">
                                        <b-form-group
                                            :label="getI18n(i18nKey, 'FIELDS.dependency_parent_question')"
                                            class="label-pdv mb-1"
                                        >
                                            <b-form-select
                                                v-model="item.parent_criterion_task_id"
                                                class="input-pdv-blue"
                                                :options="dependsOnSiblingOptions"
                                                :disabled="submitLoading || !isFormEditable"
                                            >
                                                <template v-slot:first>
                                                    <b-form-select-option :value="null" disabled>
                                                        {{ getI18n(i18nKey, 'PLACEHOLDERS.nenhuma_dependencia_questao') }}
                                                    </b-form-select-option>
                                                </template>
                                            </b-form-select>
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="3">
                                        <b-form-group
                                            :label="getI18n(i18nKey, 'FIELDS.depends_on_question_operator')"
                                            class="label-pdv mb-1"
                                        >
                                            <b-form-select
                                                v-model="item.condition_operator"
                                                class="input-pdv-blue"
                                                :options="compoundItemOperatorOptions"
                                                :disabled="submitLoading || !isFormEditable"
                                            />
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="3">
                                        <b-form-group
                                            :label="getI18n(i18nKey, 'FIELDS.depends_on_question_value')"
                                            class="label-pdv mb-1"
                                        >
                                            <b-form-input
                                                v-model="item.condition_value"
                                                class="input-pdv-blue"
                                                type="text"
                                                :disabled="submitLoading || !isFormEditable"
                                            />
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="2" class="d-flex align-items-end pb-2">
                                        <b-button
                                            v-if="compoundRule.items.length > 1"
                                            variant="outline-danger"
                                            size="sm"
                                            :disabled="submitLoading || !isFormEditable"
                                            @click="removeCompoundItem(idx)"
                                        >
                                            {{ getI18n(i18nKey, 'FIELDS.dependency_remove_item') }}
                                        </b-button>
                                    </b-col>
                                </b-row>
                            </div>

                            <b-button
                                variant="outline-primary"
                                size="sm"
                                class="mb-3"
                                :disabled="submitLoading || !isFormEditable"
                                @click="addCompoundItem"
                            >
                                + {{ getI18n(i18nKey, 'FIELDS.dependency_add_item') }}
                            </b-button>
                        </template>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="12">
                        <h6 class="text-secondary mb-2 text-uppercase small font-weight-bold">
                            {{ getI18n(i18nKey, 'TITLES.question_scoring_block') }}
                        </h6>
                    </b-col>
                </b-row>
                <b-row v-if="scoringKind === 'file'">
                    <b-col md="12">
                        <b-alert show variant="warning" class="small py-2 mb-2">
                            {{ getI18n(i18nKey, 'MESSAGES.question_rule_file_hint') }}
                        </b-alert>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="4">
                        <b-form-group
                            :label="getI18n(i18nKey, 'FIELDS.condition_operator')"
                            label-for="question-operator"
                            class="label-pdv"
                        >
                            <b-form-select
                                id="question-operator"
                                v-model="formData.condition_operator"
                                class="input-pdv-blue"
                                :options="scoringOperatorOptions"
                                :disabled="submitLoading || !isFormEditable"
                                @change="onScoringOperatorChange"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group
                            :label="scoringValueLabel"
                            label-for="question-value"
                            class="label-pdv"
                        >
                            <b-form-select
                                v-if="scoringValueUsesBooleanSelect"
                                id="question-value"
                                v-model="formData.condition_value"
                                class="input-pdv-blue"
                                :options="booleanValueOptions"
                                :disabled="submitLoading || !isFormEditable"
                            />
                            <b-form-input
                                v-else-if="scoringValueUsesNumberInput"
                                id="question-value"
                                v-model="formData.condition_value"
                                class="input-pdv-blue"
                                type="number"
                                :min="scoringKind === 'file' ? 0 : undefined"
                                :step="scoringKind === 'file' ? 1 : 'any'"
                                :disabled="submitLoading || !isFormEditable"
                            />
                            <b-form-select
                                v-else-if="scoringValueUsesChoiceSelect"
                                id="question-value"
                                v-model="formData.condition_value"
                                class="input-pdv-blue"
                                :options="scoringChoiceSelectOptions"
                                :disabled="submitLoading || !isFormEditable"
                            >
                                <template v-slot:first>
                                    <b-form-select-option :value="null" disabled>
                                        {{ getI18n(i18nKey, 'PLACEHOLDERS.selecionar_questao') }}
                                    </b-form-select-option>
                                </template>
                            </b-form-select>
                            <b-form-input
                                v-else-if="scoringKind === 'date'"
                                id="question-value"
                                v-model="formData.condition_value"
                                class="input-pdv-blue"
                                type="date"
                                :disabled="submitLoading || !isFormEditable"
                            />
                            <b-form-input
                                v-else
                                id="question-value"
                                v-model="formData.condition_value"
                                class="input-pdv-blue"
                                type="text"
                                :disabled="submitLoading || !isFormEditable"
                            />
                        </b-form-group>
                        <small v-if="scoringKind === 'unknown'" class="text-muted d-block">
                            {{ getI18n(i18nKey, 'MESSAGES.question_rule_meta_unknown') }}
                        </small>
                    </b-col>
                    <b-col md="4">
                        <b-form-group
                            :label="getI18n(i18nKey, 'TITLES.pontuacao')"
                            label-for="question-weight"
                            class="label-pdv"
                        >
                            <b-form-input
                                id="question-weight"
                                v-model.number="formData.weight"
                                class="input-pdv-blue"
                                type="number"
                                min="0"
                                step="any"
                                :disabled="submitLoading || !isFormEditable"
                            />
                            <small
                                v-if="weightValidationActive"
                                class="form-text d-block"
                                :class="payoutOverCap(formData.weight) ? 'text-danger' : 'text-muted'"
                            >
                                {{ weightCapHintForAmount(formData.weight) }}
                            </small>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="mt-3">
                    <b-col md="12">
                        <div class="d-flex align-items-center justify-content-between mb-2">
                            <h6 class="text-secondary mb-0 text-uppercase small font-weight-bold">
                                {{ getI18n(i18nKey, 'FIELDS.scoring_rules_title') }}
                            </h6>
                            <b-button
                                v-if="isFormEditable"
                                size="sm"
                                variant="outline-primary"
                                :disabled="submitLoading"
                                @click="addScoringRule"
                            >
                                + {{ getI18n(i18nKey, 'FIELDS.scoring_rule_add') }}
                            </b-button>
                        </div>
                        <small v-if="scoringRules.length === 0" class="text-muted d-block mb-2">
                            {{ getI18n(i18nKey, 'FIELDS.scoring_rules_hint') }}
                        </small>
                        <div
                            v-for="(rule, rIdx) in scoringRules"
                            :key="'sr-' + rIdx"
                            class="position-relative border rounded p-2 mb-2 scoring-rule-item"
                        >
                            <b-button
                                v-if="isFormEditable"
                                type="button"
                                variant="link"
                                class="scoring-rule-remove p-1 text-danger"
                                :disabled="submitLoading"
                                :aria-label="getI18n(i18nKey, 'FIELDS.scoring_rule_remove')"
                                @click="removeScoringRule(rIdx)"
                            >
                                <i class="fe-trash-2" aria-hidden="true"></i>
                            </b-button>
                            <b-row>
                                <b-col md="4">
                                    <b-form-group
                                        :label="getI18n(i18nKey, 'FIELDS.condition_operator')"
                                        :label-for="'scoring-rule-op-' + rIdx"
                                        class="label-pdv"
                                    >
                                        <b-form-select
                                            :id="'scoring-rule-op-' + rIdx"
                                            v-model="rule.condition_operator"
                                            class="input-pdv-blue"
                                            :options="scoringOperatorOptions"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col md="4">
                                    <b-form-group
                                        :label="scoringValueLabel"
                                        :label-for="'scoring-rule-val-' + rIdx"
                                        class="label-pdv"
                                    >
                                        <b-form-select
                                            v-if="scoringValueUsesBooleanSelect"
                                            :id="'scoring-rule-val-' + rIdx"
                                            v-model="rule.condition_value"
                                            class="input-pdv-blue"
                                            :options="booleanValueOptions"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                        <b-form-input
                                            v-else-if="scoringValueUsesNumberInput"
                                            :id="'scoring-rule-val-' + rIdx"
                                            v-model="rule.condition_value"
                                            class="input-pdv-blue"
                                            type="number"
                                            :min="scoringKind === 'file' ? 0 : undefined"
                                            :step="scoringKind === 'file' ? 1 : 'any'"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                        <b-form-select
                                            v-else-if="scoringValueUsesChoiceSelect"
                                            :id="'scoring-rule-val-' + rIdx"
                                            v-model="rule.condition_value"
                                            class="input-pdv-blue"
                                            :options="scoringChoiceSelectOptions"
                                            :disabled="submitLoading || !isFormEditable"
                                        >
                                            <template v-slot:first>
                                                <b-form-select-option :value="null" disabled>
                                                    {{ getI18n(i18nKey, 'PLACEHOLDERS.selecionar_questao') }}
                                                </b-form-select-option>
                                            </template>
                                        </b-form-select>
                                        <b-form-input
                                            v-else-if="scoringKind === 'date'"
                                            :id="'scoring-rule-val-' + rIdx"
                                            v-model="rule.condition_value"
                                            class="input-pdv-blue"
                                            type="date"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                        <b-form-input
                                            v-else
                                            :id="'scoring-rule-val-' + rIdx"
                                            v-model="rule.condition_value"
                                            class="input-pdv-blue"
                                            type="text"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col md="4">
                                    <b-form-group
                                        :label="getI18n(i18nKey, 'TITLES.pontuacao')"
                                        :label-for="'scoring-rule-w-' + rIdx"
                                        class="label-pdv"
                                    >
                                        <b-form-input
                                            :id="'scoring-rule-w-' + rIdx"
                                            v-model.number="rule.weight"
                                            class="input-pdv-blue"
                                            type="number"
                                            min="0"
                                            step="any"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                        <small
                                            v-if="weightValidationActive"
                                            class="form-text d-block"
                                            :class="payoutOverCap(rule.weight) ? 'text-danger' : 'text-muted'"
                                        >
                                            {{ weightCapHintForAmount(rule.weight) }}
                                        </small>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </div>
                    </b-col>
                </b-row>
                <div class="d-flex justify-content-end gap-2 mt-3">
                    <b-button class="btn btn-pdv-gradient-blue-transparent" :disabled="submitLoading" @click="handleClose">
                        {{ getI18n(i18nKey, 'BUTTONS.cancelar') }}
                    </b-button>
                    <b-button
                        class="btn-pdv-gradient-blue ml-3"
                        type="submit"
                        :disabled="submitLoading || !canSubmit || anyQuestionWeightOverCap || !isFormEditable"
                    >
                        <b-spinner v-if="submitLoading" small class="mr-1" />
                        {{ getI18n(i18nKey, 'BUTTONS.salvar') }}
                    </b-button>
                </div>
                </form>
            </template>
        </form-modal>

        <b-modal
            ref="deleteConfirmModal"
            centered
            size="md"
            @hidden="deleteConfirmCallback = null"
        >
            <template v-slot:modal-header>
                <h4 class="modal-title description">
                    {{ deleteConfirmTitle }}
                </h4>
                <button type="button" class="close" aria-label="Close" @click="closeDeleteConfirm">
                    <i class="fe-x-circle icon-blue" aria-hidden="true"></i>
                </button>
            </template>
            <div class="message">
                {{ deleteConfirmBody }}
            </div>
            <template v-slot:modal-footer>
                <div class="col text-center ml-3 mr-3">
                    <delete-button
                        class="mb-1"
                        :busy="deleteConfirmBusy"
                        :title="getI18n(i18nKey, 'BUTTONS.excluir')"
                        @onClick="executeDeleteConfirm"
                    />
                    <cancel-label-button @onClick="closeDeleteConfirm" />
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script>
import FormModal from '@src/components/FormModal.vue'
import EditLabelButton from '@/src/components/EditLabelButton.vue'
import DeleteLabelButton from '@/src/components/DeleteLabelButton.vue'
import DeleteButton from '@src/components/DeleteButton.vue'
import CancelLabelButton from '@src/components/CancelLabelButton.vue'
import LojaPerfeitaService from '@src/services/dashboards/LojaPerfeitaService'
import QuestionnaireItemsService from '@src/services/QuestionnaireItemsService'
import QuestionnaireFiltersService from '@src/services/QuestionnaireFiltersService'
import ProductsService from '@src/services/ProductsService'
import ProductCategoriesService from '@src/services/ProductCategoriesService'
import BrandService from '@src/services/BrandService'
import Shared from '@src/mixins/shared.vue'
import swalFeedback from '@src/mixins/swalFeedback'
import {
    defaultOperatorForKind,
    defaultValueForKind,
    extractQuestionMetaFromItem,
    formatConditionValueForDisplay,
    getQuestionKind,
    isSelectionCountOperator,
    operatorsForKind,
} from '@src/utils/lojaPerfeitaQuestionRuleUi'
import LojaPerfeitaFormControlLoadingWrap from '@src/components/dashboards/lojaPerfeita/LojaPerfeitaFormControlLoadingWrap.vue'

const i18nKey = 'LOJA_PERFEITA'
const TASK_TYPES_WITH_SCOPE = ['product', 'product_category', 'brand']

const defaultForm = () => ({
    id: 0,
    criterion_id: null,
    label: '',
    weight: 0,
    condition_operator: 'equals',
    condition_value: '',
    questionnaire_id: null,
    questionnaire_item_id: null,
    product_id: null,
    product_category_id: null,
    brand_id: null,
    depends_on_criterion_task_id: null,
    depends_on_condition_operator: 'equals',
    depends_on_condition_value: '1',
})

const defaultCompoundRule = () => ({
    logic: 'and',
    items: [{ parent_criterion_task_id: null, condition_operator: 'equals', condition_value: '1' }],
})

export default {
    components: {
        FormModal,
        EditLabelButton,
        DeleteLabelButton,
        DeleteButton,
        CancelLabelButton,
        LojaPerfeitaFormControlLoadingWrap,
    },
    mixins: [Shared, swalFeedback],
    props: {
        criterionId: { type: [String, Number], required: true },
        campaignId: { type: [String, Number], default: null },
        criterionWeight: { type: [Number, String], default: null },
        siblingQuestionsWeightSum: { type: Number, default: 0 },
        /** Other tasks in the same criterion (for optional question→question dependency). */
        siblingCriterionTasks: { type: Array, default: () => [] },
    },
    data() {
        return {
            i18nKey,
            isEdit: false,
            submitLoading: false,
            deleteConfirmTitle: '',
            deleteConfirmBody: '',
            deleteConfirmCallback: null,
            deleteConfirmBusy: false,
            formData: defaultForm(),
            questionnaires: [],
            questionnairesLoading: false,
            rosterTasks: [],
            rosterTasksLoading: false,
            rosterTasksLoaded: false,
            products: [],
            productCategories: [],
            brands: [],
            scopeOptionsLoading: false,
            /** Extra roster rows loaded for dependency parent when not in current questionnaire list */
            rosterMetaByItemId: {},
            dependsOnMetaLoading: false,
            dependencyMode: 'simple',
            compoundRule: defaultCompoundRule(),
            scoringRules: [],
        }
    },
    computed: {
        questionHasId() {
            return Boolean(this.formData.id && Number(this.formData.id) > 0)
        },
        modalTitle() {
            return this.formData.id
                ? this.getI18n(i18nKey, 'BUTTONS.editar_questao')
                : this.getI18n(i18nKey, 'BUTTONS.adicionar_questao')
        },
        /** Sentinel value for disabled dependency selects while parent question meta loads (not persisted). */
        dependencyMetaLoadingSelectValue() {
            return '__loja_perfeita_dep_meta_loading__'
        },
        dependsOnSiblingTaskRow() {
            if (!this.formData.depends_on_criterion_task_id) return null
            return (this.siblingCriterionTasks || []).find(
                (t) => Number(t.id) === Number(this.formData.depends_on_criterion_task_id)
            )
        },
        dependsOnParentRosterTask() {
            const s = this.dependsOnSiblingTaskRow
            if (!s || !s.questionnaire_item_id) return null
            const iid = Number(s.questionnaire_item_id)
            const fromRoster = this.rosterTasks.find((x) => Number(x.id) === iid)
            if (fromRoster) return fromRoster
            return this.rosterMetaByItemId[iid] || null
        },
        scoringKind() {
            const t = this.selectedTask
            if (!t || (!t.question_type && !t.question_subtype)) return 'unknown'
            return getQuestionKind(t.question_type, t.question_is_multiple, t.question_subtype)
        },
        dependencyKind() {
            const t = this.dependsOnParentRosterTask
            if (!t || (!t.question_type && !t.question_subtype)) return 'unknown'
            return getQuestionKind(t.question_type, t.question_is_multiple, t.question_subtype)
        },
        dependencyOperatorOptions() {
            return this.buildOperatorOptions(this.dependencyKind)
        },
        scoringOperatorOptions() {
            return this.buildOperatorOptions(this.scoringKind)
        },
        booleanValueOptions() {
            return [
                { value: '1', text: this.getI18n(i18nKey, 'BOOLEAN_VALUES.yes') },
                { value: '0', text: this.getI18n(i18nKey, 'BOOLEAN_VALUES.no') },
            ]
        },
        dependencyChoiceSelectOptions() {
            const opts = (this.dependsOnParentRosterTask && this.dependsOnParentRosterTask.choice_options) || []
            return opts.map((c) => ({ value: String(c.id), text: c.label }))
        },
        scoringChoiceSelectOptions() {
            const opts = (this.selectedTask && this.selectedTask.choice_options) || []
            return opts.map((c) => ({ value: String(c.id), text: c.label }))
        },
        dependencyValueUsesBooleanSelect() {
            return Boolean(this.formData.depends_on_criterion_task_id) && this.dependencyKind === 'boolean'
        },
        dependencyValueUsesNumberInput() {
            if (!this.formData.depends_on_criterion_task_id) return false
            const k = this.dependencyKind
            const op = this.formData.depends_on_condition_operator
            if (k === 'number' || k === 'file') return true
            if (k === 'choice_multi' && isSelectionCountOperator(op)) return true
            return false
        },
        dependencyValueUsesChoiceSelect() {
            if (!this.formData.depends_on_criterion_task_id) return false
            const k = this.dependencyKind
            const op = this.formData.depends_on_condition_operator
            if (k === 'choice_single') return true
            if (k === 'choice_multi' && (op === 'includes' || op === 'not_includes')) return true
            return false
        },
        scoringValueUsesBooleanSelect() {
            return Boolean(this.formData.questionnaire_item_id) && this.scoringKind === 'boolean'
        },
        scoringValueUsesNumberInput() {
            if (!this.formData.questionnaire_item_id) return false
            const k = this.scoringKind
            const op = this.formData.condition_operator
            if (k === 'number' || k === 'file') return true
            if (k === 'choice_multi' && isSelectionCountOperator(op)) return true
            return false
        },
        scoringValueUsesChoiceSelect() {
            if (!this.formData.questionnaire_item_id) return false
            const k = this.scoringKind
            const op = this.formData.condition_operator
            if (k === 'choice_single') return true
            if (k === 'choice_multi' && (op === 'includes' || op === 'not_includes')) return true
            return false
        },
        dependencyValueLabelDep() {
            if (this.dependencyKind === 'file') {
                return this.getI18n(i18nKey, 'FIELDS.condition_value_file_count')
            }
            if (this.dependencyKind === 'choice_multi' && isSelectionCountOperator(this.formData.depends_on_condition_operator)) {
                return this.getI18n(i18nKey, 'FIELDS.depends_on_question_count_value')
            }
            return this.getI18n(i18nKey, 'FIELDS.depends_on_question_value')
        },
        scoringValueLabel() {
            if (this.scoringKind === 'file') {
                return this.getI18n(i18nKey, 'FIELDS.condition_value_file_count')
            }
            if (this.scoringKind === 'choice_multi' && isSelectionCountOperator(this.formData.condition_operator)) {
                return this.getI18n(i18nKey, 'FIELDS.condition_value_count')
            }
            return this.getI18n(i18nKey, 'FIELDS.condition_value')
        },
        selectedTask() {
            if (!this.formData.questionnaire_item_id) return null
            const iid = Number(this.formData.questionnaire_item_id)
            const fromRoster = this.rosterTasks.find((t) => Number(t.id) === iid)
            if (fromRoster) return fromRoster
            return this.rosterMetaByItemId[iid] || null
        },
        selectedTaskType() {
            return this.selectedTask ? this.selectedTask.questionnaire_type : null
        },
        scopeSelectVisible() {
            return this.selectedTaskType && TASK_TYPES_WITH_SCOPE.includes(this.selectedTaskType)
        },
        scopeFieldName() {
            if (this.selectedTaskType === 'product') return 'product_id'
            if (this.selectedTaskType === 'product_category') return 'product_category_id'
            if (this.selectedTaskType === 'brand') return 'brand_id'
            return null
        },
        scopeLabel() {
            if (this.selectedTaskType === 'product') return this.getI18n(i18nKey, 'SCOPE.product')
            if (this.selectedTaskType === 'product_category') return this.getI18n(i18nKey, 'SCOPE.product_category')
            if (this.selectedTaskType === 'brand') return this.getI18n(i18nKey, 'SCOPE.brand')
            return this.getI18n(i18nKey, 'TITLES.escopo')
        },
        scopeEntityOptions() {
            if (this.selectedTaskType === 'product') {
                return (this.products || []).map((p) => ({ value: p.id, text: p.name || p.reference || `#${p.id}` }))
            }
            if (this.selectedTaskType === 'product_category') {
                return (this.productCategories || []).map((c) => ({ value: c.id, text: c.name || `#${c.id}` }))
            }
            if (this.selectedTaskType === 'brand') {
                return (this.brands || []).map((b) => ({ value: b.id, text: b.name || `#${b.id}` }))
            }
            return []
        },
        questionnaireOptions() {
            return (this.questionnaires || []).map((q) => ({
                value: q.id,
                text: q.name || q.title || `#${q.id}`,
            }))
        },
        rosterTaskOptions() {
            const fromApi = (this.rosterTasks || []).map((t) => ({
                value: t.id,
                text: t.label || `#${t.id}`,
            }))
            const selectedId = this.formData.questionnaire_item_id
            if (selectedId && this.formData.label && !fromApi.some((o) => Number(o.value) === Number(selectedId))) {
                fromApi.unshift({ value: selectedId, text: this.formData.label })
            }
            return fromApi
        },
        rosterTaskSelectState() {
            if (!this.rosterTasksLoaded) return null
            if (this.rosterTasks.length === 0) return false
            return null
        },
        canSubmit() {
            const qId = this.formData.questionnaire_id
            const itemId = this.formData.questionnaire_item_id
            return qId != null && qId !== '' && itemId != null && itemId !== ''
        },
        criterionWeightCap() {
            const w = this.criterionWeight
            if (w == null || w === '') return 0
            const n = Number(w)
            return Number.isFinite(n) ? n : 0
        },
        weightValidationActive() {
            return this.criterionWeightCap > 0
        },
        /** Function used in template as payoutOverCap(weight) — must live on computed so it is always on the instance. */
        payoutOverCap() {
            return (payoutWeight) => {
                if (!this.weightValidationActive) return false
                const s = Number(this.siblingQuestionsWeightSum) || 0
                const w = Number(payoutWeight) || 0
                return s + w > this.criterionWeightCap
            }
        },
        weightOverCap() {
            return this.payoutOverCap(this.formData.weight)
        },
        anyQuestionWeightOverCap() {
            if (!this.weightValidationActive) return false
            if (this.weightOverCap) return true
            return (this.scoringRules || []).some((r) => this.payoutOverCap(r.weight))
        },
        dependencyModeOptions() {
            return [
                { value: 'simple', text: this.getI18n(i18nKey, 'FIELDS.dependency_mode_simple') },
                { value: 'compound', text: this.getI18n(i18nKey, 'FIELDS.dependency_mode_compound') },
            ]
        },
        compoundLogicOptions() {
            return [
                { value: 'and', text: this.getI18n(i18nKey, 'FIELDS.dependency_logic_and') },
                { value: 'or', text: this.getI18n(i18nKey, 'FIELDS.dependency_logic_or') },
            ]
        },
        compoundItemOperatorOptions() {
            return [
                { value: 'equals', text: this.getI18n(i18nKey, 'CONDITION_OPERATORS.equals') },
                { value: 'not_equals', text: this.getI18n(i18nKey, 'CONDITION_OPERATORS.not_equals') },
                { value: 'gte', text: this.getI18n(i18nKey, 'CONDITION_OPERATORS.greater_than_or_equal') },
                { value: 'gt', text: this.getI18n(i18nKey, 'CONDITION_OPERATORS.greater_than') },
                { value: 'lte', text: this.getI18n(i18nKey, 'CONDITION_OPERATORS.less_than_or_equal') },
                { value: 'lt', text: this.getI18n(i18nKey, 'CONDITION_OPERATORS.less_than') },
            ]
        },
        dependsOnSiblingOptions() {
            const currentId = this.formData.id && Number(this.formData.id) > 0 ? Number(this.formData.id) : null
            return (this.siblingCriterionTasks || [])
                .filter((t) => currentId == null || Number(t.id) !== currentId)
                .map((t) => ({ value: t.id, text: t.label || `#${t.id}` }))
        },
        questionDependencySummaryLine() {
            if (!this.formData.depends_on_criterion_task_id) return ''
            const parent = (this.siblingCriterionTasks || []).find(
                (t) => Number(t.id) === Number(this.formData.depends_on_criterion_task_id)
            )
            const parentLabel = parent ? (parent.label || `#${parent.id}`) : '—'
            const opKey = this.formData.depends_on_condition_operator || 'equals'
            const op = this.getI18n(i18nKey, `CONDITION_OPERATORS.${opKey}`) || opKey
            const val = this.formatConditionValueLabel(
                this.dependencyKind,
                opKey,
                this.formData.depends_on_condition_value,
                (this.dependsOnParentRosterTask && this.dependsOnParentRosterTask.choice_options) || []
            )
            const key = this.makeI18nKey(i18nKey, 'MESSAGES.question_dependency_summary')
            return this.$t(key, { parent: parentLabel, op, value: val })
        },
        isFormEditable() {
            return !this.questionHasId || this.isEdit
        },
    },
    methods: {
        weightCapHintForAmount(payoutWeight) {
            if (!this.weightValidationActive) return ''
            const s = Number(this.siblingQuestionsWeightSum) || 0
            const w = Number(payoutWeight) || 0
            const current = s + w
            const over = current > this.criterionWeightCap
            const suffix = over
                ? 'MESSAGES.weights_questions_exceed'
                : 'MESSAGES.weights_questions_total'
            const key = this.makeI18nKey(i18nKey, suffix)
            return this.$t(key, {
                cap: Number(this.criterionWeightCap).toFixed(2),
                current: Number(current).toFixed(2),
            })
        },
        enterEditMode() {
            this.isEdit = true
            this.$nextTick(() => {
                const el = document.getElementById('question-weight')
                if (el) el.focus()
            })
        },
        buildOperatorOptions(kind) {
            const ops = operatorsForKind(kind)
            return ops.map((op) => ({
                value: op,
                text: this.getI18n(i18nKey, `CONDITION_OPERATORS.${op}`) || op,
            }))
        },
        formatConditionValueLabel(kind, operator, value, choiceOptions) {
            return formatConditionValueForDisplay(
                kind,
                operator,
                value,
                choiceOptions,
                (suffix) => this.getI18n(i18nKey, suffix)
            )
        },
        clampDependencyRule() {
            if (!this.formData.depends_on_criterion_task_id) return
            const kind = this.dependencyKind
            const ops = operatorsForKind(kind)
            let op = this.formData.depends_on_condition_operator
            if (!ops.includes(op)) {
                op = defaultOperatorForKind(kind)
                this.formData.depends_on_condition_operator = op
            }
            if (kind === 'boolean') {
                if (this.formData.depends_on_condition_value !== '0' && this.formData.depends_on_condition_value !== '1') {
                    this.formData.depends_on_condition_value = '1'
                }
            } else if (kind === 'number' || kind === 'file' || (kind === 'choice_multi' && isSelectionCountOperator(op))) {
                if (this.formData.depends_on_condition_value === '' || this.formData.depends_on_condition_value == null) {
                    this.formData.depends_on_condition_value = kind === 'file' ? '1' : '0'
                }
            } else if (kind === 'choice_single' || (kind === 'choice_multi' && (op === 'includes' || op === 'not_includes'))) {
                const choices = (this.dependsOnParentRosterTask && this.dependsOnParentRosterTask.choice_options) || []
                if (choices.length && !choices.some((c) => String(c.id) === String(this.formData.depends_on_condition_value))) {
                    this.formData.depends_on_condition_value = String(choices[0].id)
                }
            }
        },
        /**
         * Resets scoring operator/value when the user changes questionnaire or roster question.
         * (replaceModalData / loadRosterTasks still use clampScoringRule to keep saved values.)
         */
        resetScoringConditionToDefaults() {
            if (!this.formData.questionnaire_item_id) {
                this.formData.condition_operator = 'equals'
                this.formData.condition_value = ''
                return
            }
            const t = this.selectedTask
            if (!t || (!t.question_type && !t.question_subtype)) {
                this.formData.condition_operator = 'equals'
                this.formData.condition_value = ''
                return
            }
            const kind = getQuestionKind(t.question_type, t.question_is_multiple, t.question_subtype)
            this.formData.condition_operator = defaultOperatorForKind(kind)
            this.formData.condition_value = defaultValueForKind(kind)
            this.clampScoringRule()
        },
        clampScoringRule() {
            if (!this.formData.questionnaire_item_id) return
            const kind = this.scoringKind
            const ops = operatorsForKind(kind)
            let op = this.formData.condition_operator
            if (!ops.includes(op)) {
                op = defaultOperatorForKind(kind)
                this.formData.condition_operator = op
            }
            if (kind === 'boolean') {
                if (this.formData.condition_value !== '0' && this.formData.condition_value !== '1') {
                    this.formData.condition_value = '1'
                }
            } else if (kind === 'number' || kind === 'file' || (kind === 'choice_multi' && isSelectionCountOperator(op))) {
                if (this.formData.condition_value === '' || this.formData.condition_value == null) {
                    this.formData.condition_value = kind === 'file' ? '1' : '0'
                }
            } else if (kind === 'choice_single' || (kind === 'choice_multi' && (op === 'includes' || op === 'not_includes'))) {
                const choices = (this.selectedTask && this.selectedTask.choice_options) || []
                if (choices.length && !choices.some((c) => String(c.id) === String(this.formData.condition_value))) {
                    this.formData.condition_value = String(choices[0].id)
                }
            }
        },
        fetchDependsOnParentMetaIfNeeded() {
            const sibling = this.dependsOnSiblingTaskRow
            if (!sibling || !sibling.questionnaire_item_id) return Promise.resolve()
            const iid = Number(sibling.questionnaire_item_id)
            if (this.rosterTasks.some((r) => Number(r.id) === iid)) return Promise.resolve()
            if (this.rosterMetaByItemId[iid]) return Promise.resolve()
            this.dependsOnMetaLoading = true
            return QuestionnaireItemsService.fetchById(iid, { with: ['question', 'choices'] })
                .then((res) => {
                    const item = (res.data && res.data.data) || res.data || {}
                    const meta = extractQuestionMetaFromItem(item)
                    const row = {
                        id: item.id,
                        label: item.label || sibling.label || `#${item.id}`,
                        questionnaire_id: this.formData.questionnaire_id,
                        questionnaire_type: (this.selectedTask && this.selectedTask.questionnaire_type) || 'generic',
                        ...meta,
                    }
                    this.$set(this.rosterMetaByItemId, iid, row)
                })
                .catch(() => {})
                .finally(() => { this.dependsOnMetaLoading = false })
        },
        onDependencyOperatorChange() {
            this.clampDependencyRule()
        },
        onScoringOperatorChange() {
            this.clampScoringRule()
        },
        addCompoundItem() {
            this.compoundRule.items.push({ parent_criterion_task_id: null, condition_operator: 'equals', condition_value: '1' })
        },
        removeCompoundItem(idx) {
            this.compoundRule.items.splice(idx, 1)
        },
        addScoringRule() {
            this.scoringRules.push({ condition_operator: 'equals', condition_value: '', weight: 0 })
        },
        removeScoringRule(idx) {
            this.scoringRules.splice(idx, 1)
        },
        onDependsOnQuestionChange() {
            if (!this.formData.depends_on_criterion_task_id) {
                this.formData.depends_on_condition_operator = 'equals'
                this.formData.depends_on_condition_value = '1'
                return
            }
            this.fetchDependsOnParentMetaIfNeeded().then(() => this.clampDependencyRule())
        },
        openDeleteConfirm(title, body, callback) {
            this.deleteConfirmTitle = title
            this.deleteConfirmBody = body
            this.deleteConfirmCallback = callback
            this.deleteConfirmBusy = false
            this.$refs.deleteConfirmModal.show()
        },
        closeDeleteConfirm() {
            this.$refs.deleteConfirmModal.hide()
            this.deleteConfirmCallback = null
        },
        executeDeleteConfirm() {
            if (!this.deleteConfirmCallback) return
            this.deleteConfirmBusy = true
            Promise.resolve(this.deleteConfirmCallback())
                .then(() => {
                    this.closeDeleteConfirm()
                })
                .catch((err) => {
                    this.negativeFeedback(this.getI18n('RESPONSES', 'something_wrong'), err?.message)
                })
                .finally(() => {
                    this.deleteConfirmBusy = false
                })
        },
        confirmDeleteQuestion() {
            if (!this.questionHasId) return
            const id = this.formData.id
            this.openDeleteConfirm(
                this.getI18n(i18nKey, 'MESSAGES.confirm_excluir_questao_title'),
                this.getI18n(i18nKey, 'MESSAGES.confirm_excluir_questao'),
                () => LojaPerfeitaService.deleteCriterionQuestion(id).then(() => {
                    this.successFeedback(this.getI18n(i18nKey, 'TITLES.questao'), this.getI18n(i18nKey, 'MESSAGES.config_salva'))
                    this.handleClose()
                    this.$emit('refresh')
                })
            )
        },
        showModal() {
            this.submitLoading = false
            this.$refs.formModal.show()
            this.loadQuestionnaires()
            if (this.formData.questionnaire_id) {
                this.loadRosterTasks(this.formData.questionnaire_id)
            } else if (this.formData.questionnaire_item_id) {
                this.resolveQuestionnaireIdFromItem(this.formData.questionnaire_item_id)
            } else {
                this.rosterTasks = []
                this.rosterTasksLoaded = true
            }
        },
        loadQuestionnaires() {
            this.questionnairesLoading = true
            LojaPerfeitaService.getQuestionnaires()
                .then((res) => {
                    const raw = res.data
                    this.questionnaires = Array.isArray(raw) ? raw : []
                })
                .catch(() => { this.questionnaires = [] })
                .finally(() => { this.questionnairesLoading = false })
        },
        resolveQuestionnaireIdFromItem(itemId) {
            QuestionnaireItemsService.fetchById(itemId, { with: ['question', 'choices'] })
                .then((res) => {
                    const item = (res.data && res.data.data) || res.data || {}
                    const qId = item.questionnaire_id != null ? item.questionnaire_id : (item.questionnaire && item.questionnaire.id)
                    if (qId != null) {
                        this.formData.questionnaire_id = Number(qId)
                        this.loadRosterTasks(this.formData.questionnaire_id)
                    } else {
                        this.rosterTasks = []
                        this.rosterTasksLoaded = true
                    }
                })
                .catch(() => {
                    this.rosterTasks = []
                    this.rosterTasksLoaded = true
                })
        },
        onQuestionnaireChange(questionnaireId) {
            this.formData.questionnaire_item_id = null
            this.formData.product_id = null
            this.formData.product_category_id = null
            this.formData.brand_id = null
            this.rosterMetaByItemId = {}
            this.resetScoringConditionToDefaults()
            if (questionnaireId) {
                this.loadRosterTasks(questionnaireId)
            } else {
                this.rosterTasks = []
                this.rosterTasksLoaded = true
            }
        },
        loadRosterTasks(questionnaireId) {
            if (!questionnaireId) {
                this.rosterTasks = []
                this.rosterTasksLoaded = true
                return
            }
            this.rosterTasksLoading = true
            this.rosterTasksLoaded = false
            LojaPerfeitaService.getQuestionnaireTasks(questionnaireId)
                .then((res) => {
                    this.rosterMetaByItemId = {}
                    this.rosterTasks = res.data || []
                    this.rosterTasksLoaded = true
                    this.syncLabelFromRosterSelection()
                    this.clearScopeWhenTaskTypeChanges()
                    this.loadScopeOptionsIfNeeded()
                    this.$nextTick(() => {
                        this.fetchDependsOnParentMetaIfNeeded().then(() => {
                            this.clampDependencyRule()
                            this.clampScoringRule()
                        })
                    })
                })
                .catch(() => {
                    this.rosterTasks = []
                    this.rosterTasksLoaded = true
                })
                .finally(() => { this.rosterTasksLoading = false })
        },
        onRosterTaskChange(taskId) {
            this.syncLabelFromRosterSelection(taskId)
            this.clearScopeWhenTaskTypeChanges()
            this.loadScopeOptionsIfNeeded()
            this.$nextTick(() => this.resetScoringConditionToDefaults())
        },
        clearScopeWhenTaskTypeChanges() {
            const type = this.selectedTaskType
            if (type !== 'product') this.formData.product_id = null
            if (type !== 'product_category') this.formData.product_category_id = null
            if (type !== 'brand') this.formData.brand_id = null
        },
        /**
         * Fetches questionnaire filters for the selected task (all BUs) and returns allowed scope IDs.
         * Filters are per business unit: if any BU has an empty filter (no product/category/brand
         * restriction), that type is treated as unrestricted (show all). Otherwise the result is
         * the union of all IDs from all BUs that have restrictions.
         */
        fetchAllowedScopeIdsFromTaskFilters() {
            const questionnaireId = this.formData.questionnaire_id
            if (!questionnaireId) return Promise.resolve({ productIds: [], productCategoryIds: [], brandIds: [] })
            return QuestionnaireFiltersService.fetchAll({
                search: `questionnaire_id:${questionnaireId}`,
                with: ['products', 'productsCategories'],
                per_page: 999,
                page: 1,
            })
                .then((res) => {
                    const raw = (res.data && res.data.data) || res.data || []
                    const filters = Array.isArray(raw) ? raw : []
                    const productIds = new Set()
                    const productCategoryIds = new Set()
                    let anyProductUnrestricted = false
                    let anyCategoryUnrestricted = false
                    filters.forEach((f) => {
                        if (!f.has_product_restrictions || !(f.products && f.products.length)) {
                            anyProductUnrestricted = true
                        } else {
                            f.products.forEach((p) => { if (p && p.id) productIds.add(Number(p.id)) })
                        }
                        const cats = f.product_categories || f.products_categories || f.productsCategories
                        if (!f.has_product_categories_restrictions || !(Array.isArray(cats) && cats.length > 0)) {
                            anyCategoryUnrestricted = true
                        } else {
                            cats.forEach((c) => { if (c && c.id) productCategoryIds.add(Number(c.id)) })
                        }
                    })
                    return {
                        productIds: anyProductUnrestricted ? [] : Array.from(productIds),
                        productCategoryIds: anyCategoryUnrestricted ? [] : Array.from(productCategoryIds),
                        brandIds: [],
                    }
                })
                .catch(() => ({ productIds: [], productCategoryIds: [], brandIds: [] }))
        },
        loadScopeOptionsIfNeeded() {
            if (!this.scopeSelectVisible) return
            this.scopeOptionsLoading = true
            const type = this.selectedTaskType
            const baseParams = { per_page: 1000, page: 1, orderBy: 'name', sortedBy: 'asc' }
            this.fetchAllowedScopeIdsFromTaskFilters().then((allowed) => {
                if (type === 'product') {
                    ProductsService.fetchAll(baseParams)
                        .then((res) => {
                            let raw = (res.data && res.data.data) || res.data || []
                            raw = Array.isArray(raw) ? raw : []
                            this.products = allowed.productIds.length > 0
                                ? raw.filter((p) => p && allowed.productIds.includes(Number(p.id)))
                                : raw
                        })
                        .catch(() => { this.products = [] })
                        .finally(() => { this.scopeOptionsLoading = false })
                } else if (type === 'product_category') {
                    ProductCategoriesService.fetchAll(baseParams)
                        .then((res) => {
                            let raw = (res.data && res.data.data) || res.data || []
                            raw = Array.isArray(raw) ? raw : []
                            this.productCategories = allowed.productCategoryIds.length > 0
                                ? raw.filter((c) => c && allowed.productCategoryIds.includes(Number(c.id)))
                                : raw
                        })
                        .catch(() => { this.productCategories = [] })
                        .finally(() => { this.scopeOptionsLoading = false })
                } else if (type === 'brand') {
                    BrandService.fetchAll(baseParams)
                        .then((res) => {
                            let raw = (res.data && res.data.data) || res.data || []
                            raw = Array.isArray(raw) ? raw : []
                            this.brands = allowed.brandIds.length > 0
                                ? raw.filter((b) => b && allowed.brandIds.includes(Number(b.id)))
                                : raw
                        })
                        .catch(() => { this.brands = [] })
                        .finally(() => { this.scopeOptionsLoading = false })
                } else {
                    this.scopeOptionsLoading = false
                }
            })
        },
        syncLabelFromRosterSelection(taskId) {
            const id = taskId !== undefined ? taskId : this.formData.questionnaire_item_id
            if (id == null) return
            const t = this.rosterTasks.find((r) => Number(r.id) === Number(id))
            if (t && t.label) this.formData.label = t.label
        },
        replaceModalData(data) {
            const merged = { ...defaultForm(), ...data }
            if (!merged.criterion_id) merged.criterion_id = Number(this.criterionId)
            if (merged.depends_on_criterion_task_id && (merged.depends_on_condition_value == null || merged.depends_on_condition_value === '')) {
                merged.depends_on_condition_value = '1'
            }
            if (merged.depends_on_criterion_task_id && !merged.depends_on_condition_operator) {
                merged.depends_on_condition_operator = 'equals'
            }

            const apiRule = data.dependency_rule
            if (apiRule && apiRule.items && apiRule.items.length > 1) {
                this.dependencyMode = 'compound'
                this.compoundRule = {
                    logic: apiRule.logic || 'and',
                    items: apiRule.items.map((it) => ({
                        parent_criterion_task_id: it.parent_criterion_task_id,
                        condition_operator: it.condition_operator || 'equals',
                        condition_value: it.condition_value != null ? String(it.condition_value) : '1',
                    })),
                }
            } else {
                this.dependencyMode = 'simple'
                this.compoundRule = defaultCompoundRule()
            }

            const apiScoringRules = data.scoring_rules
            if (Array.isArray(apiScoringRules) && apiScoringRules.length > 0) {
                this.scoringRules = apiScoringRules.map((r) => ({
                    condition_operator: r.condition_operator || 'equals',
                    condition_value: r.condition_value != null ? String(r.condition_value) : '',
                    weight: r.weight != null ? Number(r.weight) : 0,
                }))
            } else {
                this.scoringRules = []
            }

            this.formData = merged
            this.isEdit = !(this.formData.id && Number(this.formData.id) > 0)
            this.$nextTick(() => {
                this.syncLabelFromRosterSelection()
                this.loadScopeOptionsIfNeeded()
                this.fetchDependsOnParentMetaIfNeeded().then(() => {
                    this.clampDependencyRule()
                    this.clampScoringRule()
                })
            })
        },
        handleClose() {
            this.$refs.formModal.hide()
        },
        handleSubmit() {
            if (!this.canSubmit || this.submitLoading || !this.isFormEditable || this.anyQuestionWeightOverCap) return
            this.syncLabelFromRosterSelection()
            this.submitLoading = true
            const cid = this.formData.criterion_id || this.criterionId
            const type = this.selectedTaskType
            const payload = {
                label: this.formData.label || '',
                weight: Number(this.formData.weight) || 0,
                condition_operator: this.formData.condition_operator || 'equals',
                condition_value: this.formData.condition_value != null ? String(this.formData.condition_value) : '',
                questionnaire_item_id: this.formData.questionnaire_item_id ? Number(this.formData.questionnaire_item_id) : null,
                product_id: type === 'product' && this.formData.product_id ? Number(this.formData.product_id) : null,
                product_category_id: type === 'product_category' && this.formData.product_category_id ? Number(this.formData.product_category_id) : null,
                brand_id: type === 'brand' && this.formData.brand_id ? Number(this.formData.brand_id) : null,
            }

            if (this.scoringRules.length > 0) {
                payload.scoring_rules = this.scoringRules.map((r) => ({
                    condition_operator: r.condition_operator || 'equals',
                    condition_value: r.condition_value != null ? String(r.condition_value) : '',
                    weight: Number(r.weight) || 0,
                }))
            } else {
                payload.scoring_rules = []
            }

            if (this.dependencyMode === 'compound') {
                const validItems = this.compoundRule.items.filter((it) => it.parent_criterion_task_id != null)
                if (validItems.length > 0) {
                    payload.dependency_rule = {
                        logic: this.compoundRule.logic || 'and',
                        items: validItems.map((it) => ({
                            parent_criterion_task_id: Number(it.parent_criterion_task_id),
                            condition_operator: it.condition_operator || 'equals',
                            condition_value: it.condition_value != null ? String(it.condition_value) : '',
                        })),
                    }
                }
                payload.depends_on_criterion_task_id = null
                payload.depends_on_condition_operator = null
                payload.depends_on_condition_value = null
            } else {
                payload.depends_on_criterion_task_id = this.formData.depends_on_criterion_task_id != null && this.formData.depends_on_criterion_task_id !== ''
                    ? Number(this.formData.depends_on_criterion_task_id)
                    : null
                payload.depends_on_condition_operator = this.formData.depends_on_condition_operator || 'equals'
                payload.depends_on_condition_value = this.formData.depends_on_condition_value != null
                    ? String(this.formData.depends_on_condition_value)
                    : ''
            }
            const promise = this.formData.id
                ? LojaPerfeitaService.updateCriterionQuestion(this.formData.id, payload)
                : LojaPerfeitaService.createCriterionQuestion(cid, payload)
            promise
                .then(() => {
                    this.successFeedback(this.getI18n(i18nKey, 'TITLES.questao'), this.getI18n(i18nKey, 'MESSAGES.questao_salva'))
                    this.handleClose()
                    this.$emit('refresh')
                })
                .catch((err) => {
                    const depErr = err?.response?.data?.errors?.depends_on_criterion_task_id
                    let detail = err?.response?.data?.message || err?.message || this.getI18n('RESPONSES', 'operation_not_possible')
                    if (Array.isArray(depErr) && depErr.includes('cycle')) {
                        detail = this.$t(this.makeI18nKey(i18nKey, 'MESSAGES.question_dependency_cycle'))
                    } else if (Array.isArray(depErr) && depErr.includes('invalid')) {
                        detail = this.$t(this.makeI18nKey(i18nKey, 'MESSAGES.question_dependency_invalid_parent'))
                    }
                    this.negativeFeedback(
                        this.getI18n('RESPONSES', 'something_wrong'),
                        detail
                    )
                })
                .finally(() => { this.submitLoading = false })
        },
    },
}
</script>

<style scoped>
.loja-perfeita-modal-toolbar {
    gap: 0.5rem;
    overflow-x: auto;
}

.scoring-rule-item {
    padding-right: 2.25rem;
}

.scoring-rule-remove {
    position: absolute;
    top: 0.35rem;
    right: 0.35rem;
    z-index: 1;
    line-height: 1;
}
</style>
